import { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';

/**
 * The shared-element cover-morph mechanism (Section 08's View Transitions
 * item): a list-side issue cover and a preview-screen's cover both carry
 * the *same* `view-transition-name`, so the browser morphs one into the
 * other on navigation instead of hard-cutting.
 *
 * Extracted 2026-08-19 from `reader/preview/preview.tsx` (the reader-
 * family's -- `daily`/`starmagazine`/`toyota` -- own copy, the working
 * reference implementation this generalizes) so `pages/preview-basic`
 * (`basic`/`magazine`) and `pages/preview` (`morningstar`/`poets`) can use
 * the identical mechanism instead of a second, third hand-rolled copy.
 *
 * Deliberately just the *name-passing* mechanism, not a visual opinion:
 * `previewTransitionName` only derives a provably-unique identifier (see
 * topic 08's "duplicate view-transition-name aborts the entire transition,
 * silently" quirk), and `PreviewLink` only carries it through navigation
 * state. Each template still owns its own `<img>` styling, sizing, and
 * layout entirely -- this doesn't impose one visual treatment across
 * templates whose designs differ, it only wires the plumbing every one of
 * them needs the same way.
 */
export type PreviewNavPayload = {
	preview: {
		imageSrc: string | undefined;
		transitionName: string;
	};
};

/** `id` is whatever the list already has on hand (numeric id or slug) -- not re-derived from the URL, so the list and the receiving preview screen can't disagree about what "the same issue" means. */
export function previewTransitionName(id: string | number): string {
	return `preview-${id}`;
}

export function usePreviewNavState() {
	const { location } = useRouterState();

	return (location.state as unknown as PreviewNavPayload | undefined)
		?.preview;
}

type PreviewLinkProps = {
	to: string;
	params: { id: string };
	payload: PreviewNavPayload;
	children: ReactNode;
	className?: string;
	/** Some templates fire a tracking event on this click (e.g. `basic`'s `BTN_CLICK_ISSUE`); optional since most existing usages don't need it. */
	onClick?: () => void;
	/** `from` matters when a template's issue cards are reachable from more than one route (e.g. a search/section listing as well as home) and `to` uses a relative form; most usages don't need it. */
	from?: string;
	id?: string;
};

export function PreviewLink({
	to,
	params,
	payload,
	children,
	className,
	onClick,
	from,
	id,
}: PreviewLinkProps) {
	return (
		<Link
			to={to}
			params={params}
			from={from}
			id={id}
			// Cast lives here and nowhere else -- TanStack's `state` is typed
			// per-route, and this component is deliberately reusable across
			// different templates' differently-typed routes.
			state={payload as {}}
			className={className}
			replace={false}
			viewTransition
			onClick={onClick}
		>
			{children}
		</Link>
	);
}
